import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { AppState } from "../types";
import * as dataService from "../services/dataService";
import { registerStore } from "../store";

interface AppContextType {
  state: AppState | null;
  setState: React.Dispatch<React.SetStateAction<AppState | null>>;
  loading: boolean;
  error: string | null;
  currentDate: Date;
  setCurrentDate: (d: Date) => void;
  reload: (email: string) => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {

  const [state, setState] = useState<AppState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  // --------------------------------------------------------
  // Initial empty state
  // --------------------------------------------------------

  useEffect(() => {

    const emptyState: AppState = {
      organizations: [],
      users: [],
      projects: [],
      tasks: [],
      epics: [],
      sprints: [],
      stories: [],
      retrospectives: [],
      reports: [],
      standupNotes: [],
      notifications: [],
      userRequests: [],
      currentUser: null,
      currentOrganization: null
    };

    setState(emptyState);
    setLoading(false);

  }, []);

  // --------------------------------------------------------
  // Register store bridge
  // --------------------------------------------------------

  useEffect(() => {
    if (!state) return;
    registerStore(() => state, setState);
  }, [state]);

  // --------------------------------------------------------
  // Reload data from Firestore
  // --------------------------------------------------------

  const reload = useCallback(async (email: string) => {

    if (!email) {
      console.warn("[AppContext] reload called without email");
      console.trace();
      return;
    }

    try {

      setLoading(true);
      const newState = await dataService.loadState(email);

      setState(newState);
      setError(null);
      console.log("[AppContext] State reloaded");
    } catch (err: any) {
      console.error("[AppContext] load failed:", err);
      setError(err.message || "Failed to load state");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        state,
        setState,
        loading,
        error,
        currentDate,
        setCurrentDate,
        reload,
        setError,
        setLoading
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {

  const ctx = useContext(AppContext);

  if (!ctx) {
    throw new Error("useApp must be used inside AppProvider");
  }

  return ctx;
};

export const useOptionalApp = () => useContext(AppContext);
